import { trapFocus, removeTrapFocus, waitForFlickity } from '@utilities/theme';

/**
 * CollapsibleDrawer custom element for a bottom-fixed promotional drawer.
 * Handles accessibility, Flickity slider (mobile), and overlay management.
 */
class CollapsibleDrawer extends HTMLElement {
  constructor() {
    super();
    this.toggleBtn = null;
    this.closeBtn = null;
    this.grid = null;
    this.flickityInstance = null;
    this.flickityWaitCleanup = null;
    this.imagesLoaded = false;
    this.overlay = null;

    this.onKeyUp = this.onKeyUp.bind(this);
  }

  connectedCallback() {
    this.toggleBtn = this.querySelector("[data-drawer-trigger]");
    this.closeBtn = this.querySelector("[data-drawer-close]");
    this.grid = this.querySelector("[data-drawer-grid]");

    this.overlay = document.querySelector(".collapsible-drawer__overlay");
    if (this.overlay) {
      this.overlay.addEventListener("click", () => this.closeDrawer());
    }

    // Attach Event Listeners
    if (this.toggleBtn) {
      this.toggleBtn.addEventListener("click", () => this.handleToggle());
    }

    if (this.closeBtn) {
      this.closeBtn.addEventListener("click", () => {
        this.permanentlyClose();
      });
    }

    document.body.classList.add("collapsible-drawer-exists");

    this.mobileMediaQuery = window.matchMedia("(max-width: 1023px)");
    this.updateSliderForBreakpoint(this.mobileMediaQuery);
    this._boundMediaChange = this.onMediaChange.bind(this);
    this.mobileMediaQuery.addEventListener("change", this._boundMediaChange);
  }

  disconnectedCallback() {
    if (this.mobileMediaQuery && this._boundMediaChange) {
      this.mobileMediaQuery.removeEventListener(
        "change",
        this._boundMediaChange,
      );
    }
    window.removeEventListener("keyup", this.onKeyUp);
    this.clearFlickityWait();
    if (this.overlay && this.overlay.parentNode) {
      this.overlay.remove();
    }
    document.body.classList.remove("collapsible-drawer-exists");
  }

  /**
   * Toggles drawer open/close state.
   */
  handleToggle() {
    const isExpanded = this.classList.contains("is-expanded");
    isExpanded ? this.closeDrawer() : this.openDrawer();
  }

  /**
   * Opens the drawer and sets accessibility/UI state.
   */
  openDrawer() {
    this.loadDrawerImages();

    this.classList.add("is-expanded");
    this.toggleBtn.setAttribute("aria-expanded", "true");
    this.overlay.classList.add("is-visible");

    // Prevent the background page from scrolling while interacting with drawer
    document.body.style.overflow = "hidden";

    // Listen for the Escape key to close the modal
    window.addEventListener("keyup", this.onKeyUp);

    // Refresh Flickity layout if it exists (necessary for correct height/width on hidden elements)
    if (this.flickityInstance) {
      setTimeout(() => this.flickityInstance.resize(), 300);
    } else {
      this.updateSliderForBreakpoint(this.mobileMediaQuery);
    }

    // Trap focus inside the drawer
    trapFocus(this);
  }

  loadDrawerImages() {
    if (this.imagesLoaded) return;

    const deferredImages = this.querySelectorAll("[data-drawer-image]");
    if (!deferredImages.length) {
      this.imagesLoaded = true;
      return;
    }

    for (const image of deferredImages) {
      const source = image.dataset.src;
      const sourceSet = image.dataset.srcset;

      if (sourceSet) {
        image.setAttribute("srcset", sourceSet);
      }

      if (source) {
        image.setAttribute("src", source);
      }
    }

    this.imagesLoaded = true;
  }

  /**
   * Closes the drawer and restores UI state.
   */
  closeDrawer() {
    this.classList.remove("is-expanded");
    this.toggleBtn.setAttribute("aria-expanded", "false");
    this.overlay.classList.remove("is-visible");
    document.body.style.overflow = "";

    window.removeEventListener("keyup", this.onKeyUp);

    // Remove focus trap
    removeTrapFocus(this.toggleBtn);
  }

  /**
   * Permanently hides the drawer from UI.
   */
  permanentlyClose() {
    this.closeDrawer();
    this.style.display = "none";
    if (this.overlay) {
      this.overlay.classList.remove("is-visible");
    }

    // Remove class from body when drawer is removed
    document.body.classList.remove("collapsible-drawer-exists");

    // Remove focus trap just in case
    removeTrapFocus(this.toggleBtn);
  }

  /**
   * Handles Escape key to close drawer for accessibility.
   * @param {KeyboardEvent} event
   */
  onKeyUp(event) {
    if (event.code.toUpperCase() === "ESCAPE") {
      this.closeDrawer();
      this.toggleBtn.focus(); // Return focus to the trigger for a11y
    }
  }

  /**
   * Handles media query change for Flickity slider.
   * @param {MediaQueryListEvent} event
   */
  onMediaChange(event) {
    this.updateSliderForBreakpoint(event);
  }

  /**
   * Initializes Flickity on mobile, destroys on desktop.
   * @param {MediaQueryList|MediaQueryListEvent} event
   */
  updateSliderForBreakpoint(event) {
    const isMobile = event.matches;

    if (isMobile && !this.flickityInstance && this.grid) {
      // Check if Flickity library is loaded globally
      if (typeof window.Flickity !== "undefined") {
        this.clearFlickityWait();
        this.flickityInstance = new window.Flickity(this.grid, {
          cellAlign: "left",
          contain: true,
          prevNextButtons: false,
          pageDots: false,
          percentPosition: true,
          dragThreshold: 15,
        });
      } else {
        if (!this.flickityWaitCleanup) {
          this.flickityWaitCleanup = waitForFlickity(() => {
            this.flickityWaitCleanup = null;
            this.updateSliderForBreakpoint(this.mobileMediaQuery);
          }, {
            intervalMs: 200,
            maxAttempts: 100
          });
        }
      }
    } else if (!isMobile && this.flickityInstance) {
      // Cleanup slider on desktop to restore CSS Grid layout
      this.flickityInstance.destroy();
      this.flickityInstance = null;
      this.clearFlickityWait();
    } else if (!isMobile) {
      this.clearFlickityWait();
    }
  }

  clearFlickityWait() {
    if (typeof this.flickityWaitCleanup === "function") {
      this.flickityWaitCleanup();
      this.flickityWaitCleanup = null;
    }
  }
}

if (!customElements.get("collapsible-drawer")) {
  customElements.define("collapsible-drawer", CollapsibleDrawer);
}
